import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BottomSheet from '../BottomSheet';
import { TRANSACTION_STATUS, isValidCivilDate } from '../../domain/finance';
import { useCategories } from '../../hooks/useCategories';
import { DateField, SelectField } from '../FormFields';
import './styles.css';
import { MEMBER_FILTER_ALL, buildMemberFilterOptions } from '../../domain/finance/memberAttribution';

const FiltersModal = ({
  filterStartDate,
  setFilterStartDate,
  filterEndDate,
  setFilterEndDate,
  filterStatus,
  setFilterStatus,
  filterCategory,
  setFilterCategory,
  onClose,
  resetFilters,
  allPeriods,
  setAllPeriods,
  accountId,
  onApplyPeriod,
  periodScope,
  members = [],
  transactions = [],
  memberFilter = MEMBER_FILTER_ALL,
  setMemberFilter,
}) => {
  const { categoryNames } = useCategories(accountId);
  const [localStartDate, setLocalStartDate] = useState(filterStartDate);
  const [localEndDate, setLocalEndDate] = useState(filterEndDate);
  const [localStatus, setLocalStatus] = useState(filterStatus);
  const [localCategory, setLocalCategory] = useState(filterCategory);
  const [localMemberFilter, setLocalMemberFilter] = useState(memberFilter);
  const memberOptions = useMemo(() => buildMemberFilterOptions(members, transactions), [members, transactions]);
  const [localPeriodScope, setLocalPeriodScope] = useState(
    periodScope || (filterStartDate || filterEndDate ? 'range' : allPeriods ? 'all' : 'month')
  );

  const [periodError, setPeriodError] = useState('');

  const handleApply = (event) => {
    event?.preventDefault();
    if (localPeriodScope === 'range' && (
      !isValidCivilDate(localStartDate) || !isValidCivilDate(localEndDate) || localEndDate < localStartDate
    )) {
      setPeriodError('Informe datas válidas; a data final não pode ser anterior à inicial.');
      return;
    }
    if (localPeriodScope !== 'future') {
      if (onApplyPeriod) {
        onApplyPeriod({ mode: localPeriodScope, startDate: localStartDate, endDate: localEndDate });
      } else {
        setFilterStartDate?.(localPeriodScope === 'range' ? localStartDate : '');
        setFilterEndDate?.(localPeriodScope === 'range' ? localEndDate : '');
        setAllPeriods?.(localPeriodScope === 'all');
      }
    }
    setFilterStatus(localStatus);
    setFilterCategory(localCategory);
    setMemberFilter?.(localMemberFilter);
    onClose();
  };

  const handleClear = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    setLocalStatus('');
    setLocalCategory('');
    setLocalMemberFilter(MEMBER_FILTER_ALL);
    setLocalPeriodScope('month');
    resetFilters();
    onClose();
  };

  return (
    <BottomSheet
      title="Filtros"
      ariaLabel="Filtrar movimentações"
      subtitle="Escolha o período e refine suas movimentações."
      onClose={onClose}
      size="md"
      footer={(
        <>
          <button type="submit" form="transactions-filter-form" className="nf-sheet__primary">Aplicar filtros</button>
          <button type="button" className="nf-sheet__secondary" onClick={handleClear} aria-label="Limpar">Limpar filtros</button>
        </>
      )}
    >
      <form id="transactions-filter-form" className="filters-sheet-form" onSubmit={handleApply}>
        <fieldset className="filters-sheet-group">
          <legend>Período</legend>
          <div className="filters-sheet-options">
            {[{ value: 'month', label: 'Mês exibido' }, { value: 'all', label: 'Todos' }, { value: 'range', label: 'Personalizado' }].map((option) => (
              <button type="button" key={option.value} className={localPeriodScope === option.value ? 'is-selected' : ''} aria-pressed={localPeriodScope === option.value} onClick={() => {
                setLocalPeriodScope(option.value);
                setPeriodError('');
                if (option.value !== 'range') { setLocalStartDate(''); setLocalEndDate(''); }
              }}>
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        {localPeriodScope === 'range' && <>
        <DateField
          id="filter-start-date"
          label="Data inicial"
          value={localStartDate}
          onChange={(value) => {
            setLocalStartDate(value);
            if (value) setLocalPeriodScope('range');
          }}
        />
        <DateField
          id="filter-end-date"
          label="Data final"
          value={localEndDate}
          onChange={(value) => {
            setLocalEndDate(value);
            if (value) setLocalPeriodScope('range');
          }}
        />
        </>}
        {periodError && <p role="alert">{periodError}</p>}
        <fieldset className="filters-sheet-group">
          <legend>Status</legend>
          <select className="filters-sheet-native-status" aria-label="Status" value={localStatus} onChange={(event) => setLocalStatus(event.target.value)}>
            <option value="">Todos</option><option value={TRANSACTION_STATUS.PAID}>Pago/Recebido</option><option value={TRANSACTION_STATUS.PENDING}>Pendente</option>
          </select>
          <div className="filters-sheet-options">
            {[{ value: '', label: 'Todos' }, { value: TRANSACTION_STATUS.PAID, label: 'Concluídos' }, { value: TRANSACTION_STATUS.PENDING, label: 'Pendentes' }].map((option) => (
              <button type="button" key={option.label} className={localStatus === option.value ? 'is-selected' : ''} aria-pressed={localStatus === option.value} onClick={() => setLocalStatus(option.value)}>
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        <SelectField id="filter-category" label="Categoria" value={localCategory} onChange={setLocalCategory}>
          <option value="">Todas as categorias</option>
          {!categoryNames.includes(localCategory) && localCategory && <option value={localCategory}>{localCategory}</option>}
          {categoryNames.map((category) => <option value={category} key={category}>{category}</option>)}
        </SelectField>
        {members.length > 0 && (
          <SelectField id="filter-member-attribution" label="Atribuição" value={localMemberFilter} onChange={setLocalMemberFilter}>
            {memberOptions.map((option) => <option value={option.key} key={option.key}>{option.label}{option.removed ? ' · removido' : ''}</option>)}
          </SelectField>
        )}
      </form>
    </BottomSheet>
  );
};

FiltersModal.propTypes = {
  filterStartDate: PropTypes.string,
  setFilterStartDate: PropTypes.func,
  filterEndDate: PropTypes.string,
  setFilterEndDate: PropTypes.func,
  filterStatus: PropTypes.string,
  setFilterStatus: PropTypes.func.isRequired,
  filterCategory: PropTypes.string,
  setFilterCategory: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  allPeriods: PropTypes.bool.isRequired,
  setAllPeriods: PropTypes.func,
  onApplyPeriod: PropTypes.func,
  periodScope: PropTypes.oneOf(['month', 'range', 'all', 'future']),
  onClose: PropTypes.func.isRequired,
  accountId: PropTypes.string,
  members: PropTypes.array,
  transactions: PropTypes.array,
  memberFilter: PropTypes.string,
  setMemberFilter: PropTypes.func,
};

export default FiltersModal;
